import type { HydratedDocument } from 'mongoose';
import { z } from 'zod';
import { DateSchema, ObjectIdSchema } from './base';
import type { UserDocument } from './user';

export enum SimCardStates {
  AVAILABLE = 'AVAILABLE',
  PENDING_PROVISIONING = 'PENDING_PROVISIONING',
  AKTIV = 'AKTIV',
  SUSPENDED = 'SUSPENDED',
  PENDING_DEPROVISIONING = 'PENDING_DEPROVISIONING',
  UNPROVISIONED = 'UNPROVISIONED',
}

export const SimCardNetworkSchema = z.object({
  imsi: z.string().nullable(),
  radio: z.string().optional(),
  mcc: z.string(),
  mnc: z.string(),
  lac: z.string().optional(),
  cellId: z.string().optional(),
  updatedAt: DateSchema,
});

export const SimCardSchema = z.object({
  id: ObjectIdSchema,
  iccid: z.string().max(23).min(18),
  imsi: z.string().max(15).min(6).nullish(),
  msisdn: z.string().max(15).nullable(),
  friendlyName: z.string().nullable(),
  lastIpAddress: z.string().nullable(),
  status: z.enum(SimCardStates),
  userId: ObjectIdSchema.nullable(),
  updatedAt: DateSchema,
  createdAt: DateSchema,
  group: z.string().nullable(),
  lastActivationAt: DateSchema.nullable(),
  lastTerminationAt: DateSchema.nullable(),
  lastConnectionAt: DateSchema.nullable(),
  lastDisconnectionAt: DateSchema.nullable(),
  currentDataUsage: z.number(),
  device: z
    .object({
      name: z.string(),
      id: ObjectIdSchema,
    })
    .nullish(),
  network: SimCardNetworkSchema.nullable(),
  location: z
    .object({
      lng: z.number(),
      lat: z.number(),
      updatedAt: DateSchema,
    })
    .nullable(),
  data: z
    .object({
      apn: z.string().nullish(),
      ip: z.string(),
      username: z.string().optional(),
      startedAt: DateSchema,
      stoppedAt: DateSchema.nullable(),
      updatedAt: DateSchema.nullable(),
    })
    .nullable(),
});

export type SimCard = z.infer<typeof SimCardSchema>;

interface SimCardDocumentMethods {
  canEdit(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
  canRead(user: UserDocument | Express.User | null | undefined): Promise<boolean>;
}

export type SimCardDocument = HydratedDocument<SimCard, SimCardDocumentMethods>;

export const SimCardNetworkSnapshotSchema = z
  .object({
    iccId: z.string(),
    simId: z.string(),
    network: z.object({
      imsi: z.string(),
      rat: z.string(),
      lai: z.string().nullable(), // hex string, null without a current cell
      ci: z.string().nullable(), // hex string, null without a current cell
      mcc: z.string(),
      mnc: z.string(),
      updateTime: DateSchema.nullable(),
    }),
    device: z.object({
      imei: z.string().nullable(),
      updateTime: DateSchema.nullable(),
    }),
    // null once transformed, so a parsed snapshot parses again
    location: z
      .object({
        lat: z.coerce.number().nullable(),
        lon: z.coerce.number().nullish(),
        updateTime: DateSchema.nullish(),
        lng: z.coerce.number().nullish(),
        updatedAt: DateSchema.nullish(),
      })
      .nullable(),
    data: z.object({
      apn: z.string().nullable(),
      ip: z.string(),
      username: z.string(),
      startTime: DateSchema,
      stopTime: DateSchema.nullable(),
      updateTime: DateSchema.nullable(),
    }),
    portfolioPackage: z.object({
      portfolioId: z.string(),
      packageId: z.string(),
    }),
    attributes: z.array(
      z.object({
        iccAttributeId: z.number(),
        iccId: z.string(),
        attribute: z.string(),
        value: z.string(),
        status: z.string(),
      }),
    ),
  })
  .transform(({ location, ...data }) => {
    const lng = location?.lon ?? location?.lng;
    const lat = location?.lat;
    return {
      ...data,
      network: { ...data.network, lai: toHex(data.network.lai), ci: toHex(data.network.ci) },
      location:
        lat != null && lng != null
          ? { lng, lat, updatedAt: location?.updateTime ?? location?.updatedAt ?? null }
          : null,
    };
  });

const toHex = (value: string | null) =>
  value == null || value.startsWith('0x') ? value : `0x${value}`;

export type SimCardNetworkSnapshot = z.infer<typeof SimCardNetworkSnapshotSchema>;

export const CarrierInformationSchema = z
  .object({
    mcc: z.string(),
    mnc: z.string(),
    carrierName: z.string().nullish(),
    carrier_name: z.string().nullish(),
    countryName: z.string().nullish(),
    country_name: z.string().nullish(),
    country: z.string().nullable(),
  })
  .transform((data) => ({
    mcc: data.mcc,
    mnc: data.mnc,
    carrierName: data.carrierName || data.carrier_name,
    countryName: data.countryName || data.country_name,
    country: data.country,
  }));

export type CarrierInformation = z.infer<typeof CarrierInformationSchema>;
